// Candidates strip — live grid of pending/running/done tiles for the
// current generation. Each slot animates from a placeholder with a
// progress bar through the preview frames as the model samples, then
// flips to the final image on candidate_done.
//
// "Pick" / "Open" are deferred to the gallery flow — Phase 1 keeps
// the strip read-only beyond the visual progress. Clicking a finished
// candidate opens the same detail modal as the gallery.

import type { GalleryItem } from "../../lib/rpc";
import type { CandidateSlot } from "../../lib/use-image-gen";

interface CandidatesProps {
  slots: CandidateSlot[];
  onOpen: (item: GalleryItem) => void;
}

export function Candidates({ slots, onOpen }: CandidatesProps) {
  if (slots.length === 0) return null;
  return (
    <div className="studio-candidates">
      {slots.map((s) => <Slot key={s.idx} slot={s} onOpen={onOpen} />)}
    </div>
  );
}

function Slot({ slot, onOpen }: { slot: CandidateSlot; onOpen: (item: GalleryItem) => void }) {
  const pct = Math.round(slot.progress * 100);
  const finalSrc = slot.item ? `/api/gallery-img/${slot.item.id}` : null;
  const previewSrc = slot.preview;
  const labelText =
    slot.state === "pending" ? `Candidate ${slot.idx + 1} · Waiting…`
    : slot.state === "running" ? `Candidate ${slot.idx + 1} · ${pct}%`
    : slot.state === "done"    ? `Candidate ${slot.idx + 1} · Ready`
    : `Candidate ${slot.idx + 1} · Failed`;
  return (
    <div
      className="studio-candidate"
      data-state={slot.state}
      onClick={() => slot.item && onOpen(slot.item)}
    >
      <div className="studio-candidate-img">
        {finalSrc
          ? <img src={finalSrc} alt={`candidate ${slot.idx + 1}`} />
          : previewSrc
            ? <img src={previewSrc} alt={`preview ${slot.idx + 1}`} />
            : <div className="studio-candidate-empty" />}
        {slot.state !== "done" && slot.state !== "error" && (
          <div className="studio-candidate-progress">
            <div className="studio-candidate-progress-bar" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="studio-candidate-label">{labelText}</div>
      {slot.state === "error" && slot.error && (
        <div className="studio-candidate-error">{slot.error}</div>
      )}
    </div>
  );
}
